'use client';

import { type ReactNode, useCallback, useState } from 'react';

import { ThemeProvider, ThemesEnum, ThemesModeEnum } from '@wci-tech/core-ui';

import { darkBrandColors, lightBrandColors } from '@/theme/brandColors';

import { ThemeModeContext } from './ThemeModeContext';

const STORAGE_KEY = 'raspberry-ui-theme';
const COOKIE_KEY = 'raspberry-ui-theme';

type ThemeModeProviderProps = {
  children: ReactNode;
  initialTheme: 'light' | 'dark';
};

export const ThemeModeProvider = ({ children, initialTheme }: ThemeModeProviderProps) => {
  const initialMode = initialTheme === 'dark' ? ThemesModeEnum.DARK : ThemesModeEnum.LIGHT;

  const [mode, setMode] = useState<ThemesModeEnum>(initialMode);

  const isDark = mode === ThemesModeEnum.DARK;

  /**
   * Changes the active theme mode.
   *
   * The theme update intentionally does not call window.scrollTo(),
   * requestAnimationFrame(), or sessionStorage scroll restoration.
   *
   * React re-rendering caused by the theme change must not control
   * or override the user's current scroll position.
   */
  const toggleTheme = useCallback(
    (checked: boolean) => {
      const newMode = checked ? ThemesModeEnum.DARK : ThemesModeEnum.LIGHT;

      const newTheme = checked ? 'dark' : 'light';

      /**
       * Do not update state when the requested mode is already active.
       */
      if (mode === newMode) {
        return;
      }

      /**
       * Update the document theme immediately so CSS variables
       * respond without requiring a page reload.
       */
      document.documentElement.setAttribute('data-theme', newTheme);

      /**
       * Update the single React theme state.
       *
       * ThemeProvider below receives this same mode.
       */
      setMode(newMode);

      /**
       * Persist the selected theme for future visits.
       */
      localStorage.setItem(STORAGE_KEY, newTheme);

      document.cookie =
        `${COOKIE_KEY}=${newTheme}; ` + 'Path=/; ' + 'Max-Age=31536000; ' + 'SameSite=Lax';
    },
    [mode],
  );

  return (
    <ThemeProvider
      mode={mode}
      selectedTheme={ThemesEnum.PRIMARY}
      lightBrandColors={lightBrandColors}
      darkBrandColors={darkBrandColors}
    >
      <ThemeModeContext.Provider
        value={{
          isDark,
          mode,
          toggleTheme,
        }}
      >
        {children}
      </ThemeModeContext.Provider>
    </ThemeProvider>
  );
};
